import { TabContext } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MobileTabSelect from 'components/layout/MobileTabSelect';
import React, { ReactNode, useContext } from 'react';
import {
  mobileCardSx,
  tabContainerMobileSx,
  tabContainerSx,
  tabPanelSx,
} from 'utils';
import { useTranslation } from 'react-i18next';
import { MOBILE_LAYOUT } from 'context/themeContext';
import ContentHeader from './ContentHeader';
import { ACTION_TYPE } from 'context/appContext';
import { ROUTES } from 'router';
import { AppContext } from 'context/appContext';
import { useNavigate, Link } from 'react-router';
interface VerticalTabLayoutProps<T extends string> {
  title: string;
  currentTab: T;
  tabs: T[];
  getTabRoute: (tab: T) => string;
  isLoading?: boolean;
  children: ReactNode;
  onNavigate?: (route: string, tab: T) => void;
}

export const VerticalTabLayout = <T extends string>({
  title,
  currentTab,
  tabs,
  getTabRoute,
  isLoading = false,
  children,
  onNavigate,
}: VerticalTabLayoutProps<T>): ReactNode => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { dispatch } = useContext(AppContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();

  const handleMobileTabChange = (tab: T) => {
    navigate(getTabRoute(tab));
    onNavigate?.(getTabRoute(tab), tab);
  };

  return (
    <Card
      sx={
        isMobile
          ? {
              ...mobileCardSx,
              minHeight: `calc(100dvh - ${MOBILE_LAYOUT.navbarHeight})`,
            }
          : {
              borderColor: 'secondary.main',
              borderWidth: '2px',
              borderStyle: 'solid',
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }
      }
      raised
    >
      {isMobile && <Divider sx={{ mt: 6 }} />}
      <TabContext value={currentTab}>
        <Stack
          spacing={0}
          direction={isMobile ? 'column' : 'row'}
          sx={{ height: '100%', overflow: 'hidden' }}
        >
          <Box sx={isMobile ? tabContainerMobileSx : tabContainerSx}>
            {isMobile ? (
              <MobileTabSelect
                value={currentTab}
                setValue={handleMobileTabChange}
                options={tabs}
              />
            ) : (
              <React.Fragment>
                <Box
                  sx={{
                    width: '240px',
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ContentHeader title={title} />
                </Box>
                <Box
                  sx={{
                    mr: 0.5,
                    mt: 2,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  {tabs.map((tab) => {
                    const isActive = currentTab === tab;
                    return (
                      <Link
                        key={tab}
                        to={getTabRoute(tab)}
                        onClick={(event) => {
                          if (event.defaultPrevented) return;
                          // Only fire for unmodified left-clicks that will be handled by the SPA
                          if (
                            event.button !== 0 ||
                            event.metaKey ||
                            event.altKey ||
                            event.ctrlKey ||
                            event.shiftKey
                          ) {
                            return;
                          }
                          onNavigate?.(getTabRoute(tab), tab);
                        }}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          position: 'relative',
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            alignItems: 'flex-start',
                            width: '100%',
                            p: 1,
                            minHeight: 44,
                            display: 'flex',
                            opacity: isLoading ? 0.3 : 0.7,
                            pointerEvents: isLoading ? 'none' : 'auto',
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            ...(isActive && {
                              fontWeight: 'bold',
                              opacity: 1,
                            }),
                          }}
                        >
                          <Typography
                            variant='h6'
                            color={theme.palette.secondary.contrastText}
                            fontWeight={isActive ? 'bold' : 'medium'}
                            sx={{ textTransform: 'capitalize' }}
                            noWrap
                          >
                            {t(`tabMenu.${tab}`)}
                          </Typography>
                          {isActive && (
                            <Box
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: 2,
                                backgroundColor: theme.palette.primary.main,
                              }}
                            />
                          )}
                        </Box>
                      </Link>
                    );
                  })}
                </Box>
                <Button
                  variant='text'
                  color='primary'
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    p: 2,
                  }}
                  onClick={() => {
                    dispatch({
                      type: ACTION_TYPE.LOGOUT,
                    });
                    navigate(ROUTES.LOGIN);
                  }}
                >
                  <Typography
                    variant='h6'
                    color={theme.palette.secondary.contrastText}
                    fontWeight={'bold'}
                    align={'left'}
                  >
                    {t('tabMenu.logout')}
                  </Typography>
                </Button>
              </React.Fragment>
            )}
          </Box>
          <Box
            flexGrow={1}
            minHeight={0}
            sx={{ overflow: 'hidden', backgroundColor: 'white' }}
          >
            <Box sx={tabPanelSx}>{children}</Box>
          </Box>
        </Stack>
      </TabContext>
    </Card>
  );
};
